import { CategoricalChartState } from "recharts/types/chart/types";
import dayjs from "dayjs";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { DataContext } from "src/contexts/DataContext";
import { SharedStateEvent, updateSharedState } from "src/utils/shared-state";
import { useZoomToAnimal } from "src/hooks/useZoomToAnimal";
import { useGraphHover } from "src/contexts/GraphHoverContext";

export enum ClickSelection { Start, End }

interface useDepthChartInteractionsData {
  data: any[];
  filteredData: any[];
}

interface useDepthChartInteractionsReturn {
  setScrollListener: () => void;
  removeScrollListener: () => void;
  handleClick: (e: CategoricalChartState) => void;
  onHover: (e: CategoricalChartState) => void;
  localWindowSelection: [number, number];
  playheadPress: React.MutableRefObject<boolean>;
}

const TEN_SECONDS_MS = 10 * 1000;
const ONE_HOUR_MS = 60 * 60 * 1000;
const THREE_HOURS_MS = 3 * ONE_HOUR_MS;

const roundToTenSeconds = (time: number) => {
  return Math.round(time / TEN_SECONDS_MS) * TEN_SECONDS_MS;
};

export const useDepthChartInteractions = ({
  data,
  filteredData,
}: useDepthChartInteractionsData): useDepthChartInteractionsReturn => {
  const { window: timeWindow, setWindow } = useContext(DataContext);
  const { setHoverTimestep } = useGraphHover();

  const [localWindowSelection, setLocalWindowSelection] = useState<
    [number, number]
  >([null, null]);
  const [clickSelection, setClickSelection] = useState<ClickSelection>(
    ClickSelection.Start
  );
  const hoveredIndex = useRef<number>(null);
  const [isChartHovered, setIsChartHovered] = useState<boolean>(false);
  const clickTimer = useRef<number>(null);
  const doubleClickTrue = useRef<boolean>(false);
  const controlPress = useRef<boolean>(false);
  const playheadPress = useRef<boolean>(false);
  const minTime = useMemo(() => new Date(data[0].R_Time).getTime(), [data]);
  const maxTime = useMemo(
    () => new Date(data[data.length - 1].R_Time).getTime(),
    [data]
  );

  const handleClick = useCallback(
    (e: CategoricalChartState) => {
      // if the playhead is pressed, don't do anything - drag handling is prioritized
      if (playheadPress.current) {
        return;
      }
      if (clickTimer.current && Date.now() - clickTimer.current < 250) {
        doubleClickTrue.current = true;
        if (
          timeWindow[0] === data[0].R_Time &&
          timeWindow[1] === data[data.length - 1].R_Time
        ) {
          const selectionDate = data[e.activeTooltipIndex].time;
          setWindow([
            dayjs(selectionDate - THREE_HOURS_MS).format("YYYY-MM-DD HH:mm:ss"),
            dayjs(selectionDate + THREE_HOURS_MS).format("YYYY-MM-DD HH:mm:ss"),
          ]);
        } else {
          setLocalWindowSelection([null, null]);
          setWindow([data[0].R_Time, data[data.length - 1].R_Time]);
        }
        return;
      }
      clickTimer.current = Date.now();

      setTimeout(() => {
        if (doubleClickTrue.current) {
          doubleClickTrue.current = false;
          return;
        }
        if (
          clickSelection === ClickSelection.Start ||
          !localWindowSelection[0]
        ) {
          setLocalWindowSelection([e.activeTooltipIndex, null]);
          setClickSelection(ClickSelection.End);
        } else {
          setLocalWindowSelection([
            localWindowSelection[0],
            e.activeTooltipIndex,
          ]);
          setClickSelection(ClickSelection.Start);
          const sortedLocalWindowSelection = [
            localWindowSelection[0],
            localWindowSelection[1],
          ].sort((a, b) => a - b);
          const newWindowStart = filteredData[sortedLocalWindowSelection[0]]?.R_Time;
          const newWindowEnd = filteredData[sortedLocalWindowSelection[1]]?.R_Time;
          if (newWindowStart && newWindowEnd) {
            setWindow([newWindowStart, newWindowEnd]);
          }
          setLocalWindowSelection([null, null]);
        }
      }, 250);
    },
    [clickSelection, data, filteredData, localWindowSelection, setWindow, timeWindow]
  );

  const onHover = useCallback(
    async (e: CategoricalChartState) => {
      if (e.activeTooltipIndex === hoveredIndex.current || !filteredData[e.activeTooltipIndex]) {
        return;
      }
      hoveredIndex.current = e.activeTooltipIndex;
      
      setHoverTimestep(filteredData[hoveredIndex.current].time);
      if (clickSelection === ClickSelection.End) {
        setLocalWindowSelection([
          localWindowSelection[0],
          e.activeTooltipIndex,
        ]);
      }
    },
    [clickSelection, filteredData, localWindowSelection, setHoverTimestep]
  );

  const handleWheelMoved = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();
      if (!controlPress.current) {
        return;
      }
      const delta = e.deltaY;

      try {
        const currentWindowRange =
          new Date(timeWindow[1]).getTime() - new Date(timeWindow[0]).getTime();
        if (delta < 0) {
          const newWindowRange = Math.round(currentWindowRange * 0.95);
          if (newWindowRange < ONE_HOUR_MS) {
            return;
          }
          const positionOfHover = new Date(
            filteredData[hoveredIndex.current].R_Time
          );
          const percentOfWindow =
            (positionOfHover.getTime() - new Date(timeWindow[0]).getTime()) /
            currentWindowRange;
          const startOfWindow = roundToTenSeconds(
            positionOfHover.getTime() - newWindowRange * percentOfWindow
          );
          const endOfWindow = roundToTenSeconds(
            positionOfHover.getTime() + newWindowRange * (1 - percentOfWindow)
          );
          setWindow([
            dayjs(startOfWindow).format("YYYY-MM-DD HH:mm:ss"),
            dayjs(endOfWindow).format("YYYY-MM-DD HH:mm:ss"),
          ]);
        }
        if (delta > 0) {
          const newWindowRange = Math.round(currentWindowRange * 1.1);
          
          const positionOfHover = new Date(
            filteredData[hoveredIndex.current].R_Time
          ).getTime();
          const percentOfWindow =
            (positionOfHover - new Date(timeWindow[0]).getTime()) /
            currentWindowRange;
          const startOfWindow = Math.max(
            minTime,
            roundToTenSeconds(
              positionOfHover - newWindowRange * percentOfWindow
            )
          );
          const endOfWindow = Math.min(
            maxTime,
            roundToTenSeconds(
              positionOfHover + newWindowRange * (1 - percentOfWindow)
            )
          );
          setWindow([
            dayjs(startOfWindow).format("YYYY-MM-DD HH:mm:ss"),
            dayjs(endOfWindow).format("YYYY-MM-DD HH:mm:ss"),
          ]);
        }
      } catch (e) {
        console.error(e);
      }
    },
    [filteredData, maxTime, minTime, setWindow, timeWindow]
  );

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Control") {
      controlPress.current = true;
    }
  };

  const handleKeyUp = (e: KeyboardEvent) => {
    if (e.key === "Control") {
      controlPress.current = false;
    }
  };

  useEffect(() => {
    if (isChartHovered) {
      window.addEventListener("wheel", handleWheelMoved, { passive: false });
      return () => {
        window.removeEventListener("wheel", handleWheelMoved);
      };
    }
  }, [handleWheelMoved, isChartHovered]);

  const setScrollListener = () => {
    setIsChartHovered(true);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
  };

  const removeScrollListener = () => {
    setIsChartHovered(false);
    document.removeEventListener("keydown", handleKeyDown);
    document.removeEventListener("keyup", handleKeyUp);
  };

  return {
    setScrollListener,
    removeScrollListener,
    handleClick,
    onHover,
    localWindowSelection,
    playheadPress,
  };
};

interface usePlayheadInteractionsData {
  playheadPressRef: React.MutableRefObject<boolean>;
  idx: number;
  graphData: any[];
  allData: any[];
  dataWindow: [string, string] | [];
}

export const usePlayheadInteractions = ({
  playheadPressRef,
  idx,
  allData,
  graphData,
  dataWindow,
}: usePlayheadInteractionsData) => {
  const lineGraphRef = useRef(null);
  const initXPosition = useRef<number>(null);
  const initIndex = useRef<number>(null);
  const clickTimeRef = useRef<number>(null);
  const zoomToAnimal = useZoomToAnimal();

  // Define the onDrag callback function which is memoized using useCallback to prevent unnecessary re-renders.
  const onDrag = useCallback(
    (event: MouseEvent) => {
      // Early exit if the playhead is not pressed, there's no mouse movement, or the line graph reference is not set.
      if (
        !playheadPressRef.current ||
        !event.movementX ||
        !lineGraphRef.current
      ) {
        return;
      }
      const dataToUse = dataWindow[0] && dataWindow[1] ? allData : graphData;
      // Calculate the width of the line graph minus a fixed offset (48 pixels assumed to be for padding/margins).
      const lineGraphWidth =
        lineGraphRef.current.getBoundingClientRect().width - 48;
      // Determine the number of pixels per index based on the total width divided by the number of data points.
      const pixelsPerIdx = lineGraphWidth / dataToUse.length;
      // Calculate the new index based on the mouse's current pageX position minus the initial X position,
      // scaled by the number of pixels per index.
      const newIdx =
        initIndex.current +
        Math.floor((event.pageX - initXPosition.current) / pixelsPerIdx);
      // Update the shared state with the new index value.
      updateSharedState(SharedStateEvent.IDX_UPDATE, {
        idx: newIdx,
      });
    },
    // Dependencies for useCallback, ensuring that the function is only recalculated when these values change.
    [playheadPressRef, dataWindow, allData, graphData]
  );

  // Defines the onMouseDown event handler for the playhead interaction.
  const onMouseDown = useCallback(
    (event: React.MouseEvent<SVGGElement, MouseEvent>) => {
      event.stopPropagation();
      event.preventDefault();

      playheadPressRef.current = true;
      lineGraphRef.current =
        document.getElementsByClassName("recharts-line")[0];
      clickTimeRef.current = Date.now();

      // Stores the initial mouse X position and index for later calculations.
      initXPosition.current = event.pageX;
      initIndex.current = idx;

      window.addEventListener("mousemove", onDrag);
      window.addEventListener("mouseup", () => {
        if (Date.now() - clickTimeRef.current < 250) {
          zoomToAnimal();
        }
        setTimeout(() => {
          playheadPressRef.current = false;
          lineGraphRef.current = null;
        }, 100);
      });
    },
    [playheadPressRef, idx, onDrag, zoomToAnimal]
  );

  return { onMouseDown };
};
